<template>
  <div class="modal-footer">
    <div v-if="footerProps" :class="['buttons', buttonAlignmentClass]">
      <template v-if="isRebrandingStyle">
        <Button2
          v-if="footerProps.buttonCancel" :variant="footerProps.cancelBtnVariant || ButtonVariant.TERTIARY" :text="footerProps.buttonCancel" :is-loading="isCanceling"
          @click="handleCancel"
        />
        <NewTooltip v-if="footerProps.buttonConfirm" :content="footerProps.confirmTooltip" :disabled="!footerProps.confirmTooltip">
          <Button2
            :variant="footerProps.confirmBtnVariant || ButtonVariant.PRIMARY"
            :text="footerProps.buttonConfirm"
            :disabled="!footerProps.isConfirmValid"
            :is-loading="isSubmitting"
            @click="handleConfirm"
          />
        </NewTooltip>
      </template>
      <template v-else>
        <Button
          v-if="footerProps.buttonCancel" :variant="footerProps.cancelBtnVariant || ButtonVariant.TERTIARY" :text="footerProps.buttonCancel" :is-loading="isCanceling"
          @click="handleCancel"
        />
        <NewTooltip v-if="footerProps.buttonConfirm" :content="footerProps.confirmTooltip" :disabled="!footerProps.confirmTooltip">
          <Button
            :variant="footerProps.confirmBtnVariant || ButtonVariant.PRIMARY"
            :text="footerProps.buttonConfirm"
            :disabled="!footerProps.isConfirmValid"
            :is-loading="isSubmitting"
            @click="handleConfirm"
          />
        </NewTooltip>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import Button from '@/components/common/buttons/Button.vue'
import Button2 from '@/components/common/buttons/Button2.vue'
import NewTooltip from '@/components/common/tooltips/NewTooltip.vue'
import { ButtonVariant } from '@/styles/types'
import { CustomModalProps } from '@/interfaces'
import { computed } from 'vue'
import { useStore } from 'vuex'

const store = useStore()
const emit = defineEmits(['close-action'])
const props = defineProps<CustomModalProps>()
const isSubmitting = computed(() => store.getters['modal/getIsSubmitting']() || false)
const isCanceling = computed(() => store.getters['modal/getIsCanceling']() || false)
const buttonAlignmentClass = computed(() => {
  const hasCancel = !!props.footerProps?.buttonCancel
  const hasConfirm = !!props.footerProps?.buttonConfirm
  if (hasCancel && hasConfirm) {
    return 'two-buttons'
  } else if (props.footerProps?.rightAlignSingleButton) {
    return 'right-single-button'
  } else if (props.footerProps?.leftAlignSingleButton) {
    return 'left-single-button'
  } else {
    return 'single-button'
  }
})

async function handleConfirm() {
  if (typeof props.onConfirm === 'function') {
    await props.onConfirm({ ...props })
  }
}

async function handleCancel() {
  if (typeof props.onDismiss === 'function' && !props.showDiscardStep) {
    await props.onDismiss({ ...props })
  } else {
    emit('close-action')
  }
}
</script>

<style scoped lang="scss">
.modal-footer {
  .buttons {
    margin-top: 1rem;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    &.two-buttons {
      justify-content: space-between;
    }

    &.single-button {
      justify-content: center;
      margin: 1rem 0 0;
    }

    &.right-single-button {
      justify-content: flex-end;
      margin: 1rem 0 0;
    }

    &.left-single-button {
      justify-content: flex-start;
      margin: 1rem 0 0;
    }
  }
}
</style>
